import * as React from 'react';
import { CircularProgress, Icon, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import axios from "axios";
import Autocomplete from '@material-ui/lab/Autocomplete';
import strings from './../utils/StringManager';
import LogCompanyTooltip from './LogCompanyTooltip';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
    },
    autocomnplete: {
        flex: '1',
    },
    icon: {
        color: '#888',
        marginLeft: '7px',
    },
}));

interface IOption {
    name: string,
    id: number,
    nip: string,
    regon: string,
    address: string,
    email: string,
}

interface IProps {
    value: string,
    onChange: (value: string) => void,
    className?: string,
    freeSolo?: boolean,

    errorText: string,
    isDemo: boolean,
    isLogSystemIntegrationAvailable: boolean,
}

export default function LogCompanyAutocomplete(props: IProps) {
    const classes = useStyles();

    const [selected, setSelected] = React.useState<string | IOption>(null);
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<IOption[]>([]);
    const loading = open && options.length === 0;

    React.useEffect(() => {
        if (props.value)
            setSelected(props.value);
        else
            setSelected(null);
    }, [props.value]);

    React.useEffect(() => {
        let active = true;

        if (!loading) {
            return undefined;
        }

        (async () => {
            const response = await axios.get<{ items: IOption[] }>("/LogDictionaries/Companies/");

            if (active) {
                setOptions(response.data.items);
            }
        })();

        return () => {
            active = false;
        };
    }, [loading]);

    function isVisible(option: IOption, search: string): boolean
    {
        if (option.name.toLowerCase().indexOf(search) >= 0)
            return true;
        if (option.nip?.startsWith(search))
            return true;
        if (option.regon?.startsWith(search))
            return true;

        return false;
    }

    function getString(option: IOption | string): string {
        if (typeof option === 'string') {
            return option;
        }
        else {
            return option.name;
        }
    }

    return (<div className={clsx(classes.root, props.className)}>
        <Autocomplete
            className={clsx("mi-lab-autocomplete-outlined", classes.autocomnplete)}
            
            value={selected}
            onChange={(event, newValue) => {
                setSelected(newValue);

                if (!newValue)
                    props.onChange("");
                else
                    props.onChange(getString(newValue));
            }}
            freeSolo={props.freeSolo}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            options={options}
            loading={loading}

            filterOptions={(options: IOption[], state: any) => {
                let search: string = state.inputValue;
                if (!search)
                    return options;
                search = search.toLocaleLowerCase();
                return options.filter((x) => isVisible(x, search));      
            }}
            getOptionSelected={(option, value) => {
                let optionString = getString(option);
                let valueString = getString(value);

                return optionString.toLowerCase() === valueString.toLowerCase();
            }}
            getOptionLabel={option => getString(option)}
            clearText={strings.get("clear")}
            noOptionsText={strings.get("noOptionsMessage")}
            openText={strings.get("open")}
            closeText={strings.get("close")}
            loadingText={strings.get("loading") + "..."}

            renderInput={params => (
                <TextField
                    {...params}
                    variant="outlined"
                    fullWidth
                    onChange={(event) => {
                        if (props.freeSolo) {
                            props.onChange(event.target.value);
                        }
                    }}
                    InputProps={{
                        ...params.InputProps,
                        autoComplete: 'disabled',
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
            renderOption={option => {
                let opt = option as IOption;
                return <div>
                    <Typography noWrap>{opt.name}</Typography>
                    {(opt.nip || opt.regon) && <Typography noWrap color="textSecondary" style={{ fontSize: 12 }}>
                        {opt.nip && <>NIP: {opt.nip}</>}
                        {opt.nip && opt.regon && <>, </>}
                        {opt.regon && <>REGON: {opt.regon}</>}
                    </Typography>}
                </div>
            }}
        />
        <LogCompanyAdornment value={selected} className={classes.icon} />
    </div>);
}

interface ILogCompanyAdornmentProps {
    value: IOption | string;
    className: string;
}

function LogCompanyAdornment(props: ILogCompanyAdornmentProps) {
    if (!props.value)
        return null;

    let value: string = (typeof props.value === 'string') ? props.value : props.value.name;
    return <LogCompanyTooltip value={value} className={props.className} >
        <Icon>info</Icon>
    </LogCompanyTooltip>
}